
import React, { useState } from 'react';
import { QrCode, X } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose, DialogDescription } from './ui/dialog';
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle } from './ui/drawer';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';

const QRCodeShare = () => {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();
  const { language } = useLanguage();
  
  // Use fixed domain address instead of current URL
  const url = "https://adalaturdubotsahulat.info";

  // Generate QR code image URL using a free QR code API
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`;

  // Handle share functionality for mobile devices
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: language === 'ur' ? 'عدالت بوٹ' : 'Adalat Bot',
          text: language === 'ur' ? 'قانونی دستاویز جنریٹر' : 'Legal Document Generator',
          url: url,
        });
      } catch (error) {
        console.error("Error sharing:", error);
      }
    } else {
      // Fallback - copy to clipboard
      navigator.clipboard.writeText(url)
        .then(() => {
          toast.success(language === 'ur' ? 'لنک کاپی ہو گیا ہے' : 'Link copied to clipboard');
        })
        .catch(err => {
          console.error("Error copying:", err);
        });
    }
  };

  if (isMobile) {
    return (
      <>
        <Button 
          variant="outline" 
          size="icon" 
          onClick={() => setIsOpen(true)}
          title={language === 'ur' ? 'اسکین کریں' : 'Scan QR Code'}
        >
          <QrCode size={18} />
        </Button>
        
        <Drawer open={isOpen} onOpenChange={setIsOpen}>
          <DrawerContent className="focus:outline-none">
            <DrawerHeader>
              <DrawerTitle className={`${language === 'ur' ? 'font-urdu rtl' : 'font-english ltr'}`}>
                {language === 'ur' ? 'موبائل فون پر دیکھنے کے لیے اسکین کریں' : 'Scan to view on mobile device'}
              </DrawerTitle>
              <DrawerClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </DrawerClose>
            </DrawerHeader>
            <div className="flex flex-col items-center justify-center p-4 pb-12">
              <img 
                src={qrCodeUrl} 
                alt="QR Code" 
                className="w-48 h-48 border rounded-md shadow-sm"
                onError={(e) => {
                  e.currentTarget.src = 'https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://adalaturdubotsahulat.info';
                  console.error('Failed to load QR code, using fallback');
                }}
              />
              <p className="mt-4 text-sm text-gray-500 text-center">
                {language === 'ur' ? 'اپنے موبائل فون کے کیمرے کو اس کوڈ پر رکھیں' : 'Point your phone camera at this code to view the website'}
              </p>
              <div className="mt-2 text-xs text-gray-400 truncate max-w-full overflow-hidden px-4">
                {url}
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-4" 
                onClick={handleShare}
              >
                {language === 'ur' ? 'شیئر کریں' : 'Share Link'}
              </Button>
            </div>
          </DrawerContent>
        </Drawer>
      </>
    );
  } else {
    return (
      <>
        <Button 
          variant="outline" 
          size="icon" 
          onClick={() => setIsOpen(true)}
          title={language === 'ur' ? 'اسکین کریں' : 'Scan QR Code'}
        >
          <QrCode size={18} />
        </Button>

        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="sm:max-w-md focus:outline-none">
            <DialogHeader>
              <DialogTitle className={`${language === 'ur' ? 'font-urdu rtl' : 'font-english ltr'}`}>
                {language === 'ur' ? 'موبائل فون پر دیکھنے کے لیے اسکین کریں' : 'Scan to view on mobile device'}
              </DialogTitle>
              <DialogDescription className="text-center">
                {language === 'ur' ? 'یا لنک شیئر کریں' : 'Or share this link'}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col items-center justify-center p-4">
              <img 
                src={qrCodeUrl} 
                alt="QR Code" 
                className="w-48 h-48 border rounded-md shadow-sm"
                onError={(e) => {
                  e.currentTarget.src = 'https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://adalaturdubotsahulat.info';
                  console.error('Failed to load QR code, using fallback');
                }}
              />
              <p className="mt-4 text-sm text-gray-500 text-center">
                {language === 'ur' ? 'اپنے موبائل فون کے کیمرے کو اس کوڈ پر رکھیں' : 'Point your phone camera at this code to view the website'}
              </p>
              <div className="mt-2 text-xs text-gray-400 truncate max-w-full overflow-hidden">
                {url}
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-4" 
                onClick={handleShare}
              >
                {language === 'ur' ? 'شیئر کریں' : 'Share Link'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }
};

export default QRCodeShare;
